import React, { useCallback, useEffect, useState } from "react";
import { fetchNews, markNewsViewed } from "../services/news";
import "../styles/news.css";

const PREVIEW_LIMIT = 140;

const NewsClients = ({ showCloseButton = false, onClose, onNewsViewed }) => {
    const getSeenStorageKey = useCallback(() => {
        try {
            const clientId = localStorage.getItem("clientId");
            return clientId ? `newsSeenActivityById:${clientId}` : "newsSeenActivityById";
        } catch (_) {
            return "newsSeenActivityById";
        }
    }, []);

    const [news, setNews] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [seenActivityById, setSeenActivityById] = useState(() => {
        try {
            const key = getSeenStorageKey();
            let raw = localStorage.getItem(key);

            if (!raw && key !== "newsSeenActivityById") {
                const legacyRaw = localStorage.getItem("newsSeenActivityById");
                if (legacyRaw) {
                    raw = legacyRaw;
                    try {
                        localStorage.setItem(key, legacyRaw);
                    } catch (_) {
                        // ignore
                    }
                }
            }
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch (_) {
            return {};
        }
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadNews = async () => {
            try {
                const data = await fetchNews();
                setNews(data);
            } catch (err) {
                console.error("Помилка при отриманні новин:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadNews();
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await markNewsViewed();
                if (!cancelled && typeof onNewsViewed === "function") onNewsViewed();
            } catch (_) {
                // keep UI usable even if mark-viewed fails
            }
        })();
        return () => {
            cancelled = true;
        };
        // Mark once when the section is opened; parent may pass a fresh callback.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const persistSeenMap = useCallback((updater) => {
        setSeenActivityById((prev) => {
            const base = prev && typeof prev === "object" ? prev : {};
            const next = typeof updater === "function" ? updater(base) : updater;
            try {
                localStorage.setItem(getSeenStorageKey(), JSON.stringify(next));
            } catch (_) {
                // ignore
            }
            return next;
        });
    }, [getSeenStorageKey]);

    const getActivityAtMs = (item) => {
        const raw = item?.dtlm;
        if (!raw) return null;
        const ms = new Date(raw).getTime();
        return Number.isNaN(ms) ? null : ms;
    };

    const getSeenActivityAtMsForItem = (item) => {
        const id = item?.id;
        if (!id) return null;
        const raw = seenActivityById?.[id];
        if (!raw) return null;
        const ms = new Date(raw).getTime();
        return Number.isNaN(ms) ? null : ms;
    };

    const isItemUnread = (item) => {
        const activityAt = getActivityAtMs(item);
        if (!activityAt) return false;
        const seenAt = getSeenActivityAtMsForItem(item);
        if (!seenAt) return true;
        return activityAt > seenAt;
    };

    const markItemOpened = useCallback((item) => {
        const id = item?.id;
        if (!id) return;
        const activityIso = item?.dtlm || new Date().toISOString();
        persistSeenMap((prev) => ({
            ...(prev || {}),
            [id]: activityIso,
        }));
    }, [persistSeenMap]);

    useEffect(() => {
        if (!Array.isArray(news) || news.length === 0) return;

        let raw = null;
        try {
            raw = localStorage.getItem("pendingNewsOpenId_client");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const id = parseInt(raw, 10);
        if (!Number.isFinite(id) || id <= 0) {
            try {
                localStorage.removeItem("pendingNewsOpenId_client");
            } catch (_) {
                // ignore
            }
            return;
        }

        const item = news.find((x) => Number(x?.id) === id);
        if (item) {
            try {
                localStorage.removeItem("pendingNewsOpenId_client");
            } catch (_) {
                // ignore
            }
            setExpandedId(item.id);
            markItemOpened(item);
        }
    }, [news, markItemOpened]);

    const normalizeText = (text) => (typeof text === "string" ? text : "");

    const isLongContent = (text) => {
        const raw = normalizeText(text).replace(/\r\n/g, "\n").trim();
        return raw.length > PREVIEW_LIMIT || raw.includes("\n");
    };

    const getPreviewText = (text) => {
        const raw = normalizeText(text).replace(/\r\n/g, "\n").trim();
        if (!raw) return "Без контенту";
        const singleLine = raw.replace(/\n+/g, " ");
        return singleLine.length > PREVIEW_LIMIT ? `${singleLine.slice(0, PREVIEW_LIMIT)}…` : singleLine;
    };

    const toggleExpanded = (item) => {
        const nextId = expandedId === item.id ? null : item.id;
        setExpandedId(nextId);
        if (nextId != null) {
            markItemOpened(item);
        }
    };

    if (loading) return <div className="news-popup"><p>Завантаження новин...</p></div>;
    if (error) return <div className="news-popup"><p style={{ color: "red" }}>{error}</p></div>;

    return (
        <div className="news-popup">
            {news.length === 0 ? (
                <p>Новин наразі немає</p>
            ) : (
                <div className="config-grid">
                    {news.map((item, index) => {
                        const key = item.id ?? index;
                        const expanded = expandedId === item.id;
                        const long = isLongContent(item.content);
                        return (
                            <div
                                key={key}
                                className={`wr-feed-card wr-feed-card--expandable ${isItemUnread(item) ? "news-highlight" : ""} ${expanded ? "is-expanded" : ""} ${long ? "has-more" : ""}`}
                            >
                                <button
                                    type="button"
                                    className="wr-feed-card__toggle"
                                    onClick={() => toggleExpanded(item)}
                                    aria-expanded={expanded}
                                >
                                    <div className="wr-feed-card__title">
                                        {isItemUnread(item) && <span className="news-dot" title="Нове або відредаговане" />}
                                        {item.title}
                                        {long ? (
                                            <span className="wr-feed-card__chevron" aria-hidden="true">
                                                {expanded ? "▴" : "▾"}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="wr-feed-card__meta">
                                        {new Date(item.dtlm || Date.now()).toLocaleString("uk-UA")}
                                        {long && !expanded ? <span className="wr-feed-card__more-hint">Є ще текст · натисніть, щоб розгорнути</span> : null}
                                    </div>
                                    {!expanded ? (
                                        <div className={`wr-feed-card__preview ${long ? "is-clamped" : ""}`}>
                                            {getPreviewText(item.content)}
                                        </div>
                                    ) : null}
                                </button>
                                {expanded ? (
                                    <div className="wr-feed-card__body">
                                        {normalizeText(item.content) || "Без контенту"}
                                        {long ? (
                                            <button
                                                type="button"
                                                className="wr-feed-card__collapse"
                                                onClick={() => setExpandedId(null)}
                                            >
                                                Згорнути
                                            </button>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}

            {showCloseButton && (
                <div className="news-buttons">
                    <button onClick={onClose}>❌ Закрити</button>
                </div>
            )}
        </div>
    );
};

export default NewsClients;
